// Detect if we're embedded in a native app
export let isNative = false;
export let platform = "web";
export let postNativeMessage = undefined;

export function hash_params() {
    if(!window.location.hash) {
        return {};
    }
    return Object.fromEntries(window.location.hash.replace(/^#/, "").split("&").map(p => p.split("=").map(v => v.trim())));
}

// Was the platform specified as a hash parameter?
const params = hash_params();
if(params.platform) {
    platform = params.platform;
    if(platform == "Android" || platform == "iOS") {
        isNative = true;
    }

// Otherwise infer it
} else {
    if(window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.nativeApp) {
        isNative = true;
        platform = "iOS";
    } else if(window.NativeApp && typeof window.NativeApp.postMessage === "function") {
        isNative = true;
        platform = "Android";
    }
}

if(platform == "iOS") {
    postNativeMessage = function(msg) {
        window.webkit.messageHandlers.nativeApp.postMessage(msg);
    };
} else if(platform == "Android") {
    postNativeMessage = function(msg) {
        window.NativeApp.postMessage(typeof msg === "string" ? msg : JSON.stringify(msg));
    };
}

if(isNative) {
    document.body.classList.add("isNative");
    document.body.classList.add(platform);
}

window.postNativeMessage = postNativeMessage;

export async function addScriptTag(src) {
    return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        script.onload = resolve;
        document.head.appendChild(script);
    });
}

export async function addStylesheet(href, shadow) {
    return new Promise((resolve, reject) => {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = href;
        link.onload = resolve;
        shadow.appendChild(link);
    });
}

export async function addSharedStylesheet(shadow) {
    return addStylesheet("/styles/shared-styles.css", shadow);
}

export function remToPixels(rem) {
    return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
}

export function isMobile() {
    return window.matchMedia("(max-width: 480px), (max-height: 500px)").matches;
}

export const unit_convert = {
    k_to_f: (val) => {
        return (val - 273.15) * 1.8 + 32;
    },
    f_to_k: (val) => {
        return (val - 32) / 1.8 + 273.15;
    },
    k_to_c: (val) => {
        return val - 273.15;
    },
    c_to_k: (val) => {
        return val + 273.15;
    },
    c_to_f: (val) => {
        return val * 1.8 + 32;
    },
    f_to_c: (val) => {
        return (val - 32) / 1.8;
    },
    mm_to_in: (val) => {
        return val / 25.4;
    },
    in_to_mm: (val) => {
        return val * 25.4;
    },
    mm_to_cm: (val) => {
        return val / 10.0;
    },
    cm_to_mm: (val) => {
        return val * 10.0;
    },
    cm_to_in: (val) => {
        return val / 2.54;
    },
    in_to_cm: (val) => {
        return val * 2.54;
    },
    mmps_to_inhr: (val) => {
        return val * 3600.0 / 25.4;
    },
    inhr_to_mmps: (val) => {
        return val * 25.4 / 3600.0;
    },
    mps_to_mph: (val) => {
        return val * 2.23694;
    },
    pa_to_mb: (val) => {
        return val / 100;
    }
};

export function build_unit_set(forecast_units) {
    if(!forecast_units) {
        return undefined;
    }
    return {
        temperature: (forecast_units.temperature || "F").toLowerCase(),
        wind_speed: forecast_units.wind_speed || "mph",
        distance: forecast_units.visibility || "mi",
        pressure: forecast_units.msl_pressure || "hPa",
        precipitation_rate: forecast_units.precipitation_rate || "in/hr",
        precipitation_accumulation: forecast_units.precipitation_accumulation || forecast_units.rain_accumulation || "in",
    };
}

// Converts threshold values from US units to target units
export function convert_threshold(val, units) {
    // From F
    if(units == "C") {
        return unit_convert.f_to_c(val);
    }

    // From in
    if(units == "mm/hr" || units == "mm") {
        return unit_convert.in_to_mm(val);
    }
    if(units == "cm/hr" || units == "cm") {
        return unit_convert.in_to_cm(val);
    }

    // From mph
    if(units == "kph") {
        return val * 1.60934;
    }

    return val;
}

// Convert a string to a headline with proper capitalization
const LOWERCASE_WORDS = new Set([
    "a", "an", "the",
    "and", "but", "for", "nor", "or", "so", "yet",
    "at", "by", "for", "in", "of", "off", "on", "to", "up",
    "is", "are", "was", "were", "with"
]);

function capitalize_word(word) {
    if(!word) return "";
    return word.charAt(0).toUpperCase() + word.slice(1);
}

export function capitalize_headline(headline) {
    if(!headline) {
        return "";
    }
    if(typeof headline !== "string") {
        headline = ""+headline;
    }

    // Split the headline into words
    const words = headline.toLowerCase().split(" ");

    // Always capitalize the first and last words
    words[0] = capitalize_word(words[0]);
    if(words.length > 1) {
        words[words.length - 1] = capitalize_word(words[words.length - 1]);
    }

    // Process each word according to the rules
    return words.map((word, index) => {
    // Skip first and last words as they're already handled
        if (index === 0 || index === words.length - 1) {
            return word;
        }

        // Handle hyphenated words
        if(word.includes("-")) {
            return word.split("-").map(part => capitalize_word(part)).join("-");
        }

        // Capitalize the word unless it's in our lowercase set
        return LOWERCASE_WORDS.has(word) ? word : capitalize_word(word);
    }).join(" ");
}

export function unicode_fraction(decimal, round=false) {
    if(round && decimal > 1) {
        decimal = Math.round(decimal);
    }

    if(decimal <= 0 || decimal > 1) {
        return decimal;
    }

    const fractions = [
        { decimal: 1/4, unicode: "¼" },
        { decimal: 1/2, unicode: "½" },
        { decimal: 3/4, unicode: "¾" },
        { decimal: 1/7, unicode: "⅐" },
        { decimal: 1/9, unicode: "⅑" },
        { decimal: 1/10, unicode: "⅒" },
        { decimal: 1/3, unicode: "⅓" },
        { decimal: 2/3, unicode: "⅔" },
        { decimal: 1/5, unicode: "⅕" },
        { decimal: 2/5, unicode: "⅖" },
        { decimal: 3/5, unicode: "⅗" },
        { decimal: 4/5, unicode: "⅘" },
        { decimal: 1/6, unicode: "⅙" },
        { decimal: 5/6, unicode: "⅚" },
        { decimal: 1/8, unicode: "⅛" },
        { decimal: 3/8, unicode: "⅜" },
        { decimal: 5/8, unicode: "⅝" },
        { decimal: 7/8, unicode: "⅞" }
    ];
    fractions.sort((a, b) => a.decimal - b.decimal);

    // Find the closest fraction
    let closestFraction = fractions[0];
    let smallestDiff = Math.abs(decimal - fractions[0].decimal);

    for(const fraction of fractions) {
        const diff = Math.abs(decimal - fraction.decimal);
        if (diff < smallestDiff) {
            smallestDiff = diff;
            closestFraction = fraction;
        }
    }

    // If the difference is too large, return the original number
    if (smallestDiff > 0.1) {
        return decimal;
    }

    return closestFraction.unicode;
}

// Light / Dark mode
export class ThemeManager {
    constructor() {
        this.MOBILE_BREAKPOINT = "(max-width: 480px), (max-height: 500px)";
        this.mobileQuery = window.matchMedia(this.MOBILE_BREAKPOINT);
        this.darkModeQuery = window.matchMedia("(prefers-color-scheme: dark)");
        this.callbacks = [];
        this.handleMediaQueryChange = this.handleMediaQueryChange.bind(this);
        this.mobileQuery.addEventListener("change", this.handleMediaQueryChange);
        this.darkModeQuery.addEventListener("change", this.handleMediaQueryChange);
    }

    isDark() {
        return this.mobileQuery.matches && this.darkModeQuery.matches;
    }

    isLight() {
        return !this.isDark();
    }

    onChange(callback) {
        if(typeof callback !== "function") {
            throw new Error("Callback must be a function");
        }

        this.callbacks.push(callback);

        // Return an unsubscribe function
        return () => {
            this.callbacks = this.callbacks.filter(cb => cb !== callback);
        };
    }

    handleMediaQueryChange() {
        const state = {
            isDark: this.isDark(),
            isLight: this.isLight()
        };

        this.callbacks.forEach(callback => {
            callback(state);
        });
    }

    destroy() {
        this.mobileQuery.removeEventListener("change", this.handleMediaQueryChange);
        this.darkModeQuery.removeEventListener("change", this.handleMediaQueryChange);
        this.callbacks = [];
    }
}
const THEME_MANAGER = new ThemeManager();

export function generate_map_link(field, lat, lon, zoom=3, date="now", return_path=false, location_dot=undefined) {
    let path = `/map/${field}`;

    if(lat != null && lon != null && zoom != null) {
        path += `/${lat.toFixed(4)},${lon.toFixed(4)},${zoom.toFixed(4)}`;
        if(date && date != "now") {
            path += `/${date}`;
        }
    }

    if(location_dot) {
        path += `#dot=${location_dot.lat},${location_dot.lon}`;
    }

    if(isNative) {
        path = `javascript:postNativeMessage('map:${path}');`;
    }

    if(return_path) {
        return path;
    }

    return `
    <div class="map_link">
      <a href="${path}">
       <svg class="fullscreen-button" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="4 9 4 4 9 4"></polyline>
          <polyline points="15 4 20 4 20 9"></polyline>
          <polyline points="20 15 20 20 15 20"></polyline>
          <polyline points="9 20 4 20 4 15"></polyline>
        </svg>
      </a>
    </div>
  `;
}

export function detectLongPress(element, callback, duration=500) {
    let timer;
    let touchStarted = false;

    // Prevent default context menu
    element.addEventListener("contextmenu", (e) => {
        e.preventDefault();
        return false;
    });

    // Touch start event
    element.addEventListener("touchstart", (e) => {
        e.preventDefault();

        touchStarted = true;
        timer = setTimeout(() => {
            if(touchStarted) {
                callback();
            }
        }, duration);
    });

    // Touch end event
    element.addEventListener("touchend", (e) => {
        e.preventDefault();
        touchStarted = false;
        clearTimeout(timer);
    });

    // Touch cancel event
    element.addEventListener("touchcancel", () => {
        touchStarted = false;
        clearTimeout(timer);
    });

    // Touch move event
    element.addEventListener("touchmove", (e) => {
        e.preventDefault();
        touchStarted = false;
        clearTimeout(timer);
    });
}

export function date_str_formatter(date_str, day_of_week, include_ordinal=true) {
    const [year, month_str, day_str] = date_str.split("-");
    const day_num = parseInt(day_str, 10);
    const monthIndex = parseInt(month_str, 10) - 1;

    const month_names = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    function get_ordinal_suffix(d) {
        if(!include_ordinal) {
            return "";
        }
        if (d % 10 === 1 && d !== 11) return "st";
        if (d % 10 === 2 && d !== 12) return "nd";
        if (d % 10 === 3 && d !== 13) return "rd";
        return "th";
    }

    if(!day_of_week) {
        const date = new Date(Date.UTC(year, monthIndex, day_num));
        const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
        day_of_week = days[date.getUTCDay()];
    }

    return `${day_of_week}, ${month_names[monthIndex]} ${day_num}${get_ordinal_suffix(day_num)}`;
}

export async function hashString(str) {
    const encoder = new TextEncoder();
    const data = encoder.encode(str);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
}

// Set screen size as a CSS variable
window.sizeCategory = window.sizeCategory || 0;
function update_screen_size_css() {
    const screen_width = window.innerWidth;
    const p = Math.min(1, Math.max(0, (screen_width - 360) / (430 - 360)));
    let font_size = 8.85 * (1 - p) + 10 * p;
    let content_width = 96 * (1 - p) + 93 * p;
    let header_font_size = "5rem";
    let daypart_font_size = "1.6rem";
    if(window.sizeCategory == 1) {
        font_size *= 1.2;
        header_font_size = "4.5rem";
        daypart_font_size = "1.4rem";
        content_width = 99;
    } else if(window.sizeCategory == 2) {
        font_size *= 1.3;
        content_width = 99.5;
        header_font_size = "4.25rem";
        daypart_font_size = "1.25rem";
    }
    document.documentElement.style.setProperty("font-size", font_size + "px");
    document.documentElement.style.setProperty("--content-width", content_width + "%");
    document.documentElement.style.setProperty("--header-font-size", header_font_size);
    document.documentElement.style.setProperty("--daypart-font-size", daypart_font_size);
}
if(isMobile()) {
    update_screen_size_css();
}

// Change the Accessibility size category
window.set_size_category = (val) => {
    if(val == null || val == window.sizeCategory) {
        return;
    }

    window.sizeCategory = val;
    update_screen_size_css();
    document.dispatchEvent(new CustomEvent("sizeCategoryChange", {
        detail: { sizeCategory: window.sizeCategory }
    }));
};

// Parse URL hash parameters
export function url_params() {
    const params = {};

    const params_str = window.location.search;
    if(!params_str) {
        return params;
    }

    params_str.substring(1).split("&").map(s => s.split("=")).forEach(p => params[p[0].trim()] = p[1].trim());
    return params;
}

// Show modal message
export function show_modal_message(message, duration, callback) {
    // If an existing modal is present, remove it
    const existing = document.getElementById("modal-message-overlay");
    if (existing) {
        existing.parentNode.removeChild(existing);
    }

    // Create overlay
    const overlay = document.createElement("div");
    overlay.id = "modal-message-overlay";
    overlay.classList.add("show");

    // Create message box
    const box = document.createElement("div");
    box.className = "modal-message-box";
    box.textContent = message;
    overlay.appendChild(box);

    // Append to body
    document.body.appendChild(overlay);

    let timeoutId;
    // Hide function
    function hide() {
        overlay.removeEventListener("click", hide);
        clearTimeout(timeoutId);
        overlay.classList.remove("show");
        overlay.classList.add("hide");
        // Remove after animation
        box.addEventListener("animationend", () => {
            document.body.removeChild(overlay);
            if (typeof callback === "function") {
                callback();
            }
        }, { once: true });
    }

    // Click or tap to dismiss early
    overlay.addEventListener("click", hide);

    // Auto-hide after duration
    timeoutId = setTimeout(hide, duration);
};

export function wait_for_element_condition(element, condition, timeout = 5000) {
    return new Promise((resolve, reject) => {
        const startTime = Date.now();

        function check() {
            if (condition(element)) {
                resolve(element);
            } else if (Date.now() - startTime >= timeout) {
                reject(new Error("Timeout waiting for condition"));
            } else {
                setTimeout(check, 10); // Check every 10ms
            }
        }

        check();
    });
}

export function get_hour_string(date, tz_offset) {
    if(tz_offset == null) {
        tz_offset = -new Date().getTimezoneOffset() / 60;
    }
    const targetOffsetMinutes = tz_offset * 60;
    const localOffsetMinutes = -date.getTimezoneOffset();
    const diffMinutes = targetOffsetMinutes - localOffsetMinutes;
    const shiftedDate = new Date(date.getTime() + diffMinutes * 60 * 1000);
    let hours = shiftedDate.getHours();
    const suffix = hours >= 12 ? "pm" : "am";
    hours = hours % 12;
    if(hours == null || Number.isNaN(hours)) {
        return "";
    }
    if (hours === 0) hours = 12;
    return `${hours}${suffix}`;
}

export function render_moon(phase, latitude=40, size=24) {
    const moonColor = "#C1E3F2";
    const shadowColor = THEME_MANAGER.isDark() ? "rgba(0, 0, 0, 0.65)" : "rgba(0, 0, 0, 0.5)";
    const R = size/2, cx = R, cy = R;

    // 0=new, 1=full
    const f = Math.min(1, Math.max(0, Math.abs(phase)/100));

    // angle for terminator ellipse: cosθ = 2f−1
    const cosθ = 2*f - 1;
    const Rx = R * Math.abs(cosθ);
    const Ry = R;

    let dir = phase >= 0 ? 1 : -1;
    if (latitude < 0) dir = -dir;

    // Determine if we're rendering the lit area or shadow area
    const renderingLitArea = f <= 0.5;

    // Adjust direction based on what we're rendering
    let effectiveDir = renderingLitArea ? dir : -dir;

    const sweepDisc = effectiveDir > 0 ? 1 : 0;
    const sweepEllipse = effectiveDir > 0 ? 0 : 1;

    const d = [
        `M ${cx},${cy - R}`,
        `A ${R},${R}   0 0,${sweepDisc}    ${cx},${cy + R}`,
        `A ${Rx},${Ry} 0 0,${sweepEllipse} ${cx},${cy - R}`,
        "Z"
    ].join(" ");

    if (f <= 0.5) {
        return `
        <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"
            xmlns="http://www.w3.org/2000/svg">
        <!-- full shadow base -->
        <circle cx="${cx}" cy="${cy}" r="${R}" fill="${shadowColor}" />
        <!-- lit crescent -->
        <path   d="${d}"                   fill="${moonColor}"   />
        </svg>`;
    } else {
        return `
        <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"
            xmlns="http://www.w3.org/2000/svg">
        <!-- full lit base -->
        <circle cx="${cx}" cy="${cy}" r="${R}" fill="${moonColor}"   />
        <!-- shadow gibbous -->
        <path   d="${d}"                   fill="${shadowColor}" />
        </svg>`;
    }
}

export function moon_phase_string(value) {
    const waxing = value > 0;
    const absVal = Math.abs(value);

    if (absVal < 5) return "New Moon";
    if (absVal < 25) return waxing ? "Waxing Crescent" : "Waning Crescent";
    if (absVal < 50) return waxing ? "First Quarter" : "Last Quarter";
    if (absVal < 75) return waxing ? "Waxing Gibbous" : "Waning Gibbous";
    return "Full Moon";
}

export function function_rate_limit(fn, delay) {
    let lastCall = 0;
    let timeout = null;

    return function (...args) {
        const now = Date.now();
        const remaining = delay - (now - lastCall);

        if(remaining <= 0) {
            // Call immediately
            lastCall = now;
            fn.apply(this, args);
        } else {
            // Delay until allowed
            clearTimeout(timeout);
            timeout = setTimeout(() => {
                lastCall = Date.now();
                fn.apply(this, args);
            }, remaining);
        }
    };
}

const STATE_ABBREVIATONS = {
    "Alabama": "AL",
    "Alaska": "AK",
    "Arizona": "AZ",
    "Arkansas": "AR",
    "California": "CA",
    "Colorado": "CO",
    "Connecticut": "CT",
    "Delaware": "DE",
    "Florida": "FL",
    "Georgia": "GA",
    "Hawaii": "HI",
    "Idaho": "ID",
    "Illinois": "IL",
    "Indiana": "IN",
    "Iowa": "IA",
    "Kansas": "KS",
    "Kentucky": "KY",
    "Louisiana": "LA",
    "Maine": "ME",
    "Maryland": "MD",
    "Massachusetts": "MA",
    "Michigan": "MI",
    "Minnesota": "MN",
    "Mississippi": "MS",
    "Missouri": "MO",
    "Montana": "MT",
    "Nebraska": "NE",
    "Nevada": "NV",
    "New Hampshire": "NH",
    "New Jersey": "NJ",
    "New Mexico": "NM",
    "New York": "NY",
    "North Carolina": "NC",
    "North Dakota": "ND",
    "Ohio": "OH",
    "Oklahoma": "OK",
    "Oregon": "OR",
    "Pennsylvania": "PA",
    "Rhode Island": "RI",
    "South Carolina": "SC",
    "South Dakota": "SD",
    "Tennessee": "TN",
    "Texas": "TX",
    "Utah": "UT",
    "Vermont": "VT",
    "Virginia": "VA",
    "Washington": "WA",
    "West Virginia": "WV",
    "Wisconsin": "WI",
    "Wyoming": "WY"
};

export async function query_location_name(lat, lon) {

    let response = await fetch(`/reverse_geocode?lat=${lat}&lon=${lon}`);
    response = await response.json();

    try {
        const props = response.features[0].properties;

        let parts = [];
        if(props.name && props.osm_key == "place") {
            parts.push(props.name);
        } else if(props.locality) {
            parts.push(props.locality);
        } else {
            if(props.housenumber && props.street) {
                parts.push(`${props.housenumber} ${props.street}`);
            } else if (props.street) {
                parts.push(props.street);
            }
        }

        if(props.city) {
            parts.push(props.city);
        }

        if(props.state) {
            if(STATE_ABBREVIATONS[props.state]) {
                parts.push(STATE_ABBREVIATONS[props.state]);
            } else {
                parts.push(props.state);
            }
        }

        return parts.join(", ");
    } catch {
        return undefined;
    }
}
